/**
 * prefControls — the shared control vocabulary for the T12.2 preference
 * surfaces (Household preferences / Brand preferences / Store preferences /
 * member detail). Mirrors the prototype's controls: tag chips (on/off + add),
 * the eating-style card-picker, steppers, the frequency scale, switches, and
 * the segmented choice.
 *
 * Every control is presentational + controlled — the surface owns the value and
 * the persist call (each surface auto-saves on change). All preferences are
 * SOFT signals; nothing here hard-filters.
 *
 * Styles flow from tokens (CP5). No hex, no raw px literals.
 */

import type { CSSProperties, ReactNode } from 'react';
import { setgroupStyle, h4Style, hintStyle } from '../products/settingsStyles';
import { Icon } from '../../components';

export { setgroupStyle, h4Style, hintStyle };

const HAIRLINE = '0.0625rem';

// ---------------------------------------------------------------------------
// Section group (a `.setgroup` block with heading + hint + arrow note).
// ---------------------------------------------------------------------------

export const arrowNoteStyle: CSSProperties = {
  margin: 'var(--space-3) 0 0',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 'var(--line-height-normal)',
};

// ---------------------------------------------------------------------------
// Tag chips — on/off toggle (diet, store categories) + like/avoid (brands).
// ---------------------------------------------------------------------------

export const tagsRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  alignItems: 'center',
};

const tagBaseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-pill)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  cursor: 'pointer',
  minHeight: '2.25rem',
};

// Tag ON — accent text on the violet tint, no visible border (prototype `.tag`).
const tagOnStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
  borderColor: 'transparent',
};

const tagOffStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-muted)',
  borderColor: 'var(--color-border-strong)',
};

// Like — success text on the mint tint, no border (prototype `.tag.like`).
const tagLikeStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-status-success-subtle)',
  color: 'var(--color-status-success)',
  borderColor: 'transparent',
};

// Avoid — danger text on the rose tint, no border (prototype `.tag.avoid`).
const tagAvoidStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-status-danger-subtle)',
  color: 'var(--color-status-danger)',
  borderColor: 'transparent',
};

const tagXStyle: CSSProperties = {
  opacity: 0.6,
  cursor: 'pointer',
  lineHeight: 1,
};

/** An on/off toggle chip — used for diet exclusions + store categories. */
export function TagToggle({
  label,
  on,
  onToggle,
  testId,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
  testId: string;
}): JSX.Element {
  return (
    <span
      role="button"
      tabIndex={0}
      aria-pressed={on}
      style={on ? tagOnStyle : tagOffStyle}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      data-testid={testId}
      data-on={on || undefined}
    >
      {on && (
        <span aria-hidden="true" style={{ fontSize: 'var(--font-size-xs)' }}>
          <Icon name="check" />
        </span>
      )}
      {label}
    </span>
  );
}

/**
 * A removable like/avoid chip — used for household brand/product preferences.
 * MN5: the leading glyph + the remove control are the shared `<Icon>` (★ → a
 * filled star for like, 🚫 → a ban mark for avoid, ✕ → close), not emoji/text.
 */
export function RemovableChip({
  label,
  variant,
  onRemove,
  testId,
}: {
  label: string;
  variant: 'like' | 'avoid';
  onRemove: () => void;
  testId: string;
}): JSX.Element {
  return (
    <span
      style={variant === 'like' ? tagLikeStyle : tagAvoidStyle}
      data-testid={testId}
      data-preference={variant}
    >
      <span aria-hidden="true">
        <Icon name={variant === 'like' ? 'star-filled' : 'ban'} />
      </span>
      {label}
      <span
        role="button"
        tabIndex={0}
        aria-label={`Remove ${label}`}
        style={tagXStyle}
        onClick={onRemove}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRemove();
          }
        }}
        data-testid={`${testId}-remove`}
      >
        <Icon name="close" />
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Stepper row (composition: adults / kids).
// ---------------------------------------------------------------------------

const setrowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3) 0',
};

const setrowLabelStyle: CSSProperties = { flex: 1, minWidth: 0 };
const setrowTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};
const setrowDescStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

const stepperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
  flex: '0 0 auto',
};

const stepperBtnStyle: CSSProperties = {
  border: 0,
  backgroundColor: 'var(--color-bg-muted)',
  width: '2.75rem',
  height: '2.75rem',
  fontSize: 'var(--font-size-md)',
  cursor: 'pointer',
  color: 'var(--color-text-primary)',
  display: 'grid',
  placeItems: 'center',
};

const stepperValueStyle: CSSProperties = {
  minWidth: '2.75rem',
  textAlign: 'center',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
};

export function StepperRow({
  title,
  desc,
  value,
  onStep,
  testId,
}: {
  title: string;
  desc?: string;
  value: number;
  onStep: (delta: number) => void;
  testId: string;
}): JSX.Element {
  return (
    <div style={setrowStyle}>
      <div style={setrowLabelStyle}>
        <div style={setrowTitleStyle}>{title}</div>
        {desc && <div style={setrowDescStyle}>{desc}</div>}
      </div>
      <div style={stepperStyle}>
        <button
          type="button"
          style={stepperBtnStyle}
          aria-label={`Fewer ${title}`}
          onClick={() => onStep(-1)}
          data-testid={`${testId}-decrement`}
        >
          −
        </button>
        <span style={stepperValueStyle} data-testid={`${testId}-value`}>
          {value}
        </span>
        <button
          type="button"
          style={stepperBtnStyle}
          aria-label={`More ${title}`}
          onClick={() => onStep(1)}
          data-testid={`${testId}-increment`}
        >
          +
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card-picker (eating style) — the distinct control, visually unlike chips.
// ---------------------------------------------------------------------------

const stylecardsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const stylecardBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-3)',
  cursor: 'pointer',
  backgroundColor: 'var(--color-bg-primary)',
  width: '100%',
  textAlign: 'left',
};

// Selected eating-style card — accent border on the violet tint (prototype
// `.stylecard.on`). The icon tile picks up the tint too (`.stylecard.on .ic`).
const stylecardOnStyle: CSSProperties = {
  ...stylecardBaseStyle,
  borderColor: 'var(--color-accent-primary)',
  backgroundColor: 'var(--color-accent-subtle)',
};

const stylecardIconStyle: CSSProperties = {
  width: '2.375rem',
  height: '2.375rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-lg)',
  flex: '0 0 auto',
};

const stylecardIconOnStyle: CSSProperties = {
  ...stylecardIconStyle,
  backgroundColor: 'var(--color-accent-subtle)',
};

// Title + description are block so they stack on their own lines (prototype
// `.stylecard .t` / `.d`). As inline spans they concatenated ("Cooks from
// scratchMostly raw…") — T12.2 step 11c.
const stylecardTitleStyle: CSSProperties = {
  display: 'block',
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

const stylecardDescStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
  lineHeight: 'var(--line-height-normal)',
};

const pickDotStyle: CSSProperties = {
  width: '1.25rem',
  height: '1.25rem',
  borderRadius: '50%',
  borderWidth: '0.125rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  flex: '0 0 auto',
  position: 'relative',
};

const pickDotOnStyle: CSSProperties = {
  ...pickDotStyle,
  borderColor: 'var(--color-accent-primary)',
};

export interface CardOption {
  value: string;
  icon: string;
  title: string;
  desc: string;
}

export function CardPicker({
  options,
  value,
  onPick,
  testId,
}: {
  options: CardOption[];
  value: string | null;
  onPick: (value: string) => void;
  testId: string;
}): JSX.Element {
  return (
    <div style={stylecardsStyle} role="radiogroup" data-testid={testId}>
      {options.map((opt) => {
        const on = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            role="radio"
            aria-checked={on}
            style={on ? stylecardOnStyle : stylecardBaseStyle}
            onClick={() => onPick(opt.value)}
            data-testid={`${testId}-${opt.value}`}
            data-on={on || undefined}
          >
            <span
              style={on ? stylecardIconOnStyle : stylecardIconStyle}
              aria-hidden="true"
            >
              {opt.icon}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={stylecardTitleStyle}>{opt.title}</span>
              <span style={stylecardDescStyle}>{opt.desc}</span>
            </span>
            <span
              style={on ? pickDotOnStyle : pickDotStyle}
              aria-hidden="true"
            >
              {on && (
                <span
                  style={{
                    position: 'absolute',
                    inset: '0.1875rem',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-accent-primary)',
                  }}
                />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scale (cooking frequency) + segmented Choice (age group / units).
// ---------------------------------------------------------------------------

const scaleStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-1)',
};

const scaleItemStyle: CSSProperties = {
  flex: 1,
  textAlign: 'center',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  padding: 'var(--space-3) var(--space-1)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  backgroundColor: 'var(--color-bg-primary)',
  minHeight: '2.75rem',
};

const scaleItemOnStyle: CSSProperties = {
  ...scaleItemStyle,
  backgroundColor: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderColor: 'var(--color-accent-primary)',
};

export interface ScaleOption {
  value: string;
  label: string;
}

export function Scale({
  options,
  value,
  onPick,
  testId,
}: {
  options: ScaleOption[];
  value: string | null;
  onPick: (value: string) => void;
  testId: string;
}): JSX.Element {
  return (
    <div style={scaleStyle} role="radiogroup" data-testid={testId}>
      {options.map((opt) => {
        const on = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            role="radio"
            aria-checked={on}
            style={on ? scaleItemOnStyle : scaleItemStyle}
            onClick={() => onPick(opt.value)}
            data-testid={`${testId}-${opt.value}`}
            data-on={on || undefined}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const choiceStyle: CSSProperties = {
  display: 'inline-flex',
  gap: '0.125rem',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-pill)',
  padding: '0.1875rem',
  flexWrap: 'wrap',
};

const choiceItemStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-pill)',
  cursor: 'pointer',
  border: 0,
  backgroundColor: 'transparent',
  minHeight: '2.25rem',
};

const choiceItemOnStyle: CSSProperties = {
  ...choiceItemStyle,
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

export function Choice({
  options,
  value,
  onPick,
  testId,
}: {
  options: ScaleOption[];
  value: string | null;
  onPick: (value: string) => void;
  testId: string;
}): JSX.Element {
  return (
    <div style={choiceStyle} role="radiogroup" data-testid={testId}>
      {options.map((opt) => {
        const on = value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            role="radio"
            aria-checked={on}
            style={on ? choiceItemOnStyle : choiceItemStyle}
            onClick={() => onPick(opt.value)}
            data-testid={`${testId}-${opt.value}`}
            data-on={on || undefined}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Switch (travel / notifications / GPS).
// ---------------------------------------------------------------------------

const switchRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) 0',
};

const switchTrackStyle: CSSProperties = {
  width: '2.875rem',
  height: '1.75rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-border-strong)',
  position: 'relative',
  flex: '0 0 auto',
  cursor: 'pointer',
  border: 0,
  padding: 0,
};

const switchTrackOnStyle: CSSProperties = {
  ...switchTrackStyle,
  backgroundColor: 'var(--color-accent-primary)',
};

const switchKnobStyle: CSSProperties = {
  position: 'absolute',
  top: '0.1875rem',
  left: '0.1875rem',
  width: '1.375rem',
  height: '1.375rem',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-primary)',
  transition: 'transform 0.18s',
};

export function SwitchRow({
  title,
  desc,
  on,
  onToggle,
  testId,
}: {
  title: string;
  desc?: ReactNode;
  on: boolean;
  onToggle: () => void;
  testId: string;
}): JSX.Element {
  return (
    <div style={switchRowStyle}>
      <div style={setrowLabelStyle}>
        <div style={setrowTitleStyle}>{title}</div>
        {desc && <div style={setrowDescStyle}>{desc}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={title}
        style={on ? switchTrackOnStyle : switchTrackStyle}
        onClick={onToggle}
        data-testid={testId}
        data-on={on || undefined}
      >
        <span
          style={{
            ...switchKnobStyle,
            transform: on ? 'translateX(1.125rem)' : 'none',
          }}
        />
      </button>
    </div>
  );
}
